<template>
    <VJoyPanel :id="anchorsPageProfile.educationSection" class="profile-educations" padding="large">
        <template #panel-title>
            <h2>{{ t('profile.section.educations.title.v2') }}</h2>
        </template>

        <template #panel-title-action>
            <LazyEducationTranslation v-if="isTranslationActivated && educationSectionState.length > 0" />
            <LazyEducationEditionTopActions v-if="isEditionEnabled && educationSectionState.length > 0" />
        </template>

        <template #panel-body>
            <template v-if="educationSectionState.length > 0">
                <LazyEducationListDraggable v-if="isEditionEnabled" />
                <LazyEducationList v-else />
                <ShowMoreByItemCount
                    test-id="toggle-show-all-educations"
                    :total="educationsCount"
                    :limit="EDUCATIONS_TO_DISPLAY_BY_DEFAULT"
                    :label="t('profile.section.educations.show.all.label.v2', {count: educationsCount})"
                    :locked="{
                        allowHref: true,
                        label: t('profile.section.educations.private', [profile?.firstname]),
                        componentName: 'profile-education-section',
                        targetId: anchorsPageProfile.educationSection,
                    }"
                    @expand="showEducations(true)"
                    @collapse="showEducations(false)"
                />
            </template>
            <EmptyContentSection
                v-else-if="isEditionEnabled"
                icon="school"
                :title="t('profile.section.educations.empty.content.title')"
                :description="t('profile.section.educations.empty.content.description')"
            >
                <template #action>
                    <ClientOnly>
                        <VJoyButton icon="add" size="small" variant="secondary" @click="openAddEducation">
                            {{ t('profile.section.educations.add') }}
                        </VJoyButton>
                        <template #fallback>
                            <VJoyFormFieldSkeleton size="small" width="250px" />
                        </template>
                    </ClientOnly>
                </template>
            </EmptyContentSection>
        </template>
    </VJoyPanel>
</template>

<script setup lang="ts">
    import {storeToRefs} from 'pinia';
    import {LazyEducationEditionTopActions, LazyEducationList, LazyEducationListDraggable, LazyEducationTranslation} from '#components';
    import {computed, useProfilePageViewMode, useTranslation, ref, watch} from '#imports';
    import {VJoyPanel, VJoyButton, VJoyFormFieldSkeleton} from '@maltjoy/core-vue';
    import ShowMoreByItemCount from '~/components/_common/show-more/ShowMoreByItemCount.vue';
    import EmptyContentSection from '~/components/section/EmptyContentSection.vue';
    import {anchorsPageProfile} from '~/config/anchors';
    import {eventBus} from '~/config/events';
    import {EditionOpenedEntryPoint} from '~/config/TrackingEvents';
    import {useEducationSectionState} from '~/features/education/composables/useEducationSectionState';
    import {useProfileStore} from '~/store/profileStore';
    import {useTranslationStore} from '~/store/translation/translationStore';
    import {educationMapper} from '~/types/infra/EducationMapper';
    import type {EducationRequest} from '~/types/infra/EducationRequest';

    const EDUCATIONS_TO_DISPLAY_BY_DEFAULT = 2;

    // Composables
    const {t} = useTranslation();
    const {isEditionEnabled} = useProfilePageViewMode();

    // State
    const educationSectionState = useEducationSectionState();

    // Stores
    const {educations, profile} = storeToRefs(useProfileStore());
    const {isTranslationActivated} = storeToRefs(useTranslationStore());
    const isShowingAllEducations = ref<boolean>(false);

    const educationsCount = computed<number>(() => educations.value?.items.length || 0);
    const educationRequests = computed<EducationRequest[]>(
        () => educations.value?.items.map((education, index) => educationMapper.toRequest(education, index)) || [],
    );
    const firstFewEducationRequests = computed<EducationRequest[]>(() => educationRequests.value.slice(0, EDUCATIONS_TO_DISPLAY_BY_DEFAULT));

    function initDraggableList() {
        educationSectionState.value = isShowingAllEducations.value ? educationRequests.value : firstFewEducationRequests.value;
    }

    function showEducations(showAll: boolean): void {
        isShowingAllEducations.value = showAll;
        initDraggableList();
    }

    initDraggableList();

    watch(
        () => educations.value?.items,
        () => {
            initDraggableList();
        },
    );

    function openAddEducation() {
        eventBus.emit('educations:add-mode', {entryPoint: EditionOpenedEntryPoint.PROFILE_PAGE});
    }
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .profile-educations {
        scroll-margin-top: calc(var(--header-menu-height) + var(--joy-core-spacing-2));
    }

    .profile-educations__list {
        list-style-type: none;
        margin: 0;
        padding-left: 0;
    }
</style>
